/* React error boundary — catches render-time errors in a subtree so a
 * single component crash doesn't blank the whole page.
 *
 * Why a class: React only supports error boundaries via the class API
 * (componentDidCatch + getDerivedStateFromError). There's no hook
 * equivalent. Library wrappers exist but adding a dep for ~40 lines
 * of code isn't worth it.
 *
 * Where to wrap:
 *   - Around each top-level route in App.tsx so a crash in /agent-desk
 *     can't take down /inbound or /settings.
 *   - Around each pane in AgentDesk so the transcript pane crashing
 *     doesn't break the canvas + softphone.
 *
 * The fallback UI is intentionally plain — no app chrome, no auto-
 * retry, no telemetry. The agent should refresh; if the bug repeats,
 * the next action is to check the browser console for the captured
 * error.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = {
  /** What's wrapped. The boundary doesn't render this when an error
   *  is caught — only the fallback. */
  children: ReactNode;
  /** Short label shown in the fallback UI ("Agent Desk", "Transcript",
   *  etc.) so an agent looking at a half-broken page knows which
   *  pane crashed. */
  label?: string;
};

type State = {
  error: Error | null;
};

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Capture context for the browser console — the user-facing
    // fallback stays plain, but a developer opening DevTools sees
    // the component stack alongside the error.
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.label ? `:${this.props.label}` : ""}]`,
      error,
      info.componentStack,
    );
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children;
    return (
      <div className="error-boundary" role="alert">
        <div className="error-boundary-title">
          Something went wrong
          {this.props.label ? ` in ${this.props.label}` : ""}.
        </div>
        <div className="error-boundary-message">
          {this.state.error.message || "Unknown error"}
        </div>
        <div className="error-boundary-actions">
          <button
            type="button"
            className="error-boundary-retry"
            onClick={this.reset}
          >
            Try again
          </button>
          <button
            type="button"
            className="error-boundary-reload"
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
